import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, X, Trash2, Copy, Send, RefreshCw, Upload, Film } from "lucide-react";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50";
const STATUS_STYLE = {
  draft: "bg-sand-200 text-charcoal-600", in_review: "bg-clay-100 text-clay-800",
  approved: "bg-sage/15 text-sage", scheduled: "bg-sage/15 text-sage-700",
  published: "bg-sage text-white", rejected: "bg-sand-200 text-charcoal-400", failed: "bg-red-100 text-red-700",
};
const PLATFORM_STYLE = {
  facebook: "bg-[#1877F2]/10 text-[#1877F2]", instagram: "bg-[#E4405F]/10 text-[#E4405F]",
  tiktok: "bg-charcoal-800/10 text-charcoal-800", x: "bg-charcoal-900/10 text-charcoal-900",
  linkedin: "bg-[#0A66C2]/10 text-[#0A66C2]", youtube: "bg-[#FF0000]/10 text-[#CC0000]",
};
const PLATFORM_LABELS = {
  facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok",
  x: "X (Twitter)", linkedin: "LinkedIn", youtube: "YouTube Shorts",
};
const PLATFORMS = Object.keys(PLATFORM_LABELS);

export default function SocialManager() {
  const [tab, setTab] = useState("queue");
  return (
    <div data-testid="admin-social">
      <div className="flex gap-2 mb-6">
        {[["queue", "Content queue"], ["insights", "Reporting"], ["connections", "Connections"], ["settings", "Settings"]].map(([id, label]) => (
          <button key={id} data-testid={`social-tab-${id}`} onClick={() => setTab(id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === id ? "bg-charcoal-800 text-white" : "bg-sand-100 text-charcoal-600 hover:bg-sand-200"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "queue" && <QueueTab />}
      {tab === "insights" && <InsightsTab />}
      {tab === "connections" && <ConnectionsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function ConnectionsBanner({ connections }) {
  const [open, setOpen] = useState(false);
  if (!connections) return null;
  const missing = connections.filter((c) => !c.connected);
  if (missing.length === 0) return null;
  return (
    <div data-testid="social-connections-banner" className="mb-5 rounded-2xl border-2 border-clay-300 bg-clay-100/40 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-clay-900">
          Auto-publish not connected for: {missing.map((c) => c.label).join(", ")}.
        </p>
        <button data-testid="social-connections-toggle" onClick={() => setOpen((v) => !v)}
          className="text-xs font-medium text-terracotta underline underline-offset-2">
          {open ? "Hide setup instructions" : "How do I connect each platform?"}
        </button>
      </div>
      <p className="mt-1 text-xs text-charcoal-600">
        Approving a post keeps it safely in the queue with a notice — nothing publishes until the platform is connected.
        Connected platforms publish automatically on approval.
      </p>
      {open && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {connections.map((c) => (
            <div key={c.platform} data-testid={`social-conn-${c.platform}`}
              className={`rounded-xl border px-4 py-3 ${c.connected ? "border-sage/40 bg-sage-light/40" : "border-sand-200 bg-white"}`}>
              <p className="text-sm font-medium text-charcoal-800">
                {c.label} — {c.connected ? "✓ Connected" : "Not connected"}
              </p>
              {!c.connected && (
                <>
                  <p className="mt-1 text-xs text-charcoal-500">Needs: {c.missing.join(", ")}</p>
                  <p className="mt-1 text-xs text-charcoal-600">{c.help} Share the credentials with Emergent to add them to the backend environment.</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QueueTab() {
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState({ status: "", platform: "" });
  const [busy, setBusy] = useState(false);
  const [connections, setConnections] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (filter.status) params.set("status", filter.status);
    if (filter.platform) params.set("platform", filter.platform);
    api.get(`/admin/social/posts?${params}`).then((r) => setPosts(r.data));
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/admin/social/connections").then((r) => setConnections(r.data)).catch(() => {}); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/social/generate", { count: 2 });
      toast.success("2 new drafts generated — review them below");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    setBusy(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <SectionTitle title="Content queue" subtitle="AI drafts land here for your approval — nothing goes out without it, on any platform. Images and video are auto-sized per platform at publish time." />
        <button data-testid="social-generate" onClick={generate} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm font-medium text-white hover:bg-terracotta-hover disabled:opacity-60">
          <Sparkles size={15} className={busy ? "animate-pulse" : ""} /> {busy ? "Generating…" : "Generate 2 drafts now"}
        </button>
      </div>
      <ConnectionsBanner connections={connections} />
      <div className="flex gap-2 mb-5">
        <select data-testid="social-filter-status" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className={`${FIELD} w-44`}>
          <option value="">All statuses</option>
          {["in_review", "approved", "scheduled", "published", "rejected", "failed"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select data-testid="social-filter-platform" value={filter.platform} onChange={(e) => setFilter({ ...filter, platform: e.target.value })} className={`${FIELD} w-44`}>
          <option value="">All platforms</option>
          {PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABELS[p]}</option>)}
        </select>
      </div>
      {!posts ? <p className="text-charcoal-500">Loading…</p> :
        posts.length === 0 ? <Card><p className="text-sm text-charcoal-500">No posts yet — click "Generate 2 drafts now" to get started.</p></Card> : (
          <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} reload={load} />)}</div>
        )}
    </div>
  );
}

function MediaPreview({ p }) {
  if (p.media_type === "video" && p.video_url) {
    return (
      <video data-testid={`social-video-${p.id}`} src={p.video_url} controls preload="metadata"
        className="mb-3 max-h-64 w-72 rounded-xl border border-sand-200 bg-charcoal-900 object-contain" />
    );
  }
  if (p.image_path || p.image_url) {
    return (
      <a href={p.image_path || p.image_url} target="_blank" rel="noreferrer" download>
        <img src={p.image_path || p.image_url} alt="creative" className="mb-3 h-40 w-40 rounded-xl object-cover border border-sand-200" data-testid={`social-image-${p.id}`} />
      </a>
    );
  }
  return null;
}

function PostCard({ post, reload }) {
  const [p, setP] = useState(post);
  const [liveUrl, setLiveUrl] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => setP(post), [post]);

  const save = async () => {
    await api.put(`/admin/social/posts/${p.id}`, p);
    toast.success("Post updated");
    reload();
  };
  const setStatus = async (status, extra = {}) => {
    try {
      const { data } = await api.post(`/admin/social/posts/${p.id}/status`, { status, ...extra });
      if (data.status === "published") toast.success(`Published to ${PLATFORM_LABELS[p.platform] || p.platform}!`);
      else if (data.warning) toast.warning(data.warning.replace("NOT_CONNECTED: ", "Approved, but auto-publish is not connected — "));
      else if (data.error) toast.error(`Publish failed: ${data.error}`);
      else toast.success(`Marked ${data.status.replace("_", " ")}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [publishing, setPublishing] = useState(false);
  const publishNow = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/admin/social/posts/${p.id}/publish`);
      if (data.status === "published") toast.success(`Published to ${PLATFORM_LABELS[p.platform] || p.platform}!`);
      else toast.error(data.error?.replace("NOT_CONNECTED: ", "Not connected — ") || "Publish failed");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    setPublishing(false);
  };
  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/social/posts/${p.id}`);
    reload();
  };
  const copyAll = () => {
    navigator.clipboard.writeText(`${p.caption}\n\n${p.cta}\n\n${p.hashtags}`);
    toast.success("Caption + CTA + hashtags copied");
  };
  const [genning, setGenning] = useState(false);
  const [genningVid, setGenningVid] = useState(false);
  const genImage = async () => {
    setGenning(true);
    try {
      await api.post(`/admin/social/posts/${p.id}/generate-image`);
      toast.success("Creative generated");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed"); }
    setGenning(false);
  };
  const genVideo = async () => {
    setGenningVid(true);
    try {
      await api.post(`/admin/social/posts/${p.id}/generate-video`);
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data } = await api.get(`/admin/social/posts/${p.id}`);
        if (data.video_status === "ready") {
          toast.success("Branded clip ready — preview it below");
          reload();
          setGenningVid(false);
          return;
        }
        if (data.video_status === "failed") {
          toast.error("Video generation failed — try again");
          setGenningVid(false);
          return;
        }
      }
      toast.warning("Still rendering — refresh the queue in a minute");
    } catch (e) { toast.error(e?.response?.data?.detail || "Video generation failed"); }
    setGenningVid(false);
  };
  const uploadMedia = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) return toast.error("File is over 100MB — please compress it first");
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/admin/social/posts/${p.id}/media`, body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.media_type === "video" ? "Video attached — preview it below" : "Image attached");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const editable = ["in_review", "draft", "rejected", "failed"].includes(p.status);

  return (
    <Card data-testid={`social-post-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PLATFORM_STYLE[p.platform] || "bg-sand-200 text-charcoal-600"}`}>
          {PLATFORM_LABELS[p.platform] || p.platform}
        </span>
        {p.media_type === "video" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-charcoal-800 px-2.5 py-0.5 text-xs font-medium text-white"><Film size={11} /> video</span>
        )}
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[p.status]}`} data-testid={`social-status-${p.id}`}>{p.status.replace("_", " ")}</span>
        {p.pillar && <span className="rounded-full bg-sand-100 px-2.5 py-0.5 text-xs text-charcoal-500">{p.pillar}</span>}
        <span className="ml-auto text-xs text-charcoal-400">
          scheduled {p.scheduled_at?.replace("T", " ")}
          {p.approved_at && <> · approved {p.approved_at.slice(0, 16).replace("T", " ")}</>}
          {p.published_at && <> · posted {p.published_at.slice(0, 16).replace("T", " ")}</>}
        </span>
      </div>
      {p.error && (
        <p className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" data-testid={`social-error-${p.id}`}>
          {p.error.replace("NOT_CONNECTED: ", "Auto-publish not connected — ")}
        </p>
      )}
      <MediaPreview p={p} />
      {editable ? (
        <div className="space-y-2">
          <textarea rows={3} className={FIELD} value={p.caption} onChange={(e) => setP({ ...p, caption: e.target.value })} data-testid={`social-caption-${p.id}`} />
          <input className={FIELD} value={p.cta} onChange={(e) => setP({ ...p, cta: e.target.value })} placeholder="CTA" />
          <input className={FIELD} value={p.hashtags} onChange={(e) => setP({ ...p, hashtags: e.target.value })} placeholder="#hashtags" />
          <div className="flex flex-wrap gap-2">
            <select data-testid={`social-platform-${p.id}`} className={`${FIELD} w-44`} value={p.platform}
              onChange={(e) => setP({ ...p, platform: e.target.value })}>
              {PLATFORMS.map((pl) => <option key={pl} value={pl}>{PLATFORM_LABELS[pl]}</option>)}
            </select>
            <input type="datetime-local" className={`${FIELD} w-56`} value={p.scheduled_at || ""} onChange={(e) => setP({ ...p, scheduled_at: e.target.value })} />
            <input className={`${FIELD} flex-1 min-w-[180px]`} value={p.image_url || ""} disabled={p.media_type === "video"}
              onChange={(e) => setP({ ...p, image_url: e.target.value, ...(e.target.value ? { media_type: "image" } : {}) })}
              placeholder={p.media_type === "video" ? "Video attached — upload replaces it" : "Image URL (or upload below)"} />
          </div>
          {p.image_brief && <p className="text-xs text-charcoal-400">Suggested creative: {p.image_brief}</p>}
          <p className="text-xs text-charcoal-400">
            Captions are auto-adapted to each platform's limit (X 280 · Instagram/TikTok 2,200 · LinkedIn 3,000). Media is auto-cropped/transcoded per platform at publish time.
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-charcoal-800 whitespace-pre-wrap">{p.caption}</p>
          <p className="text-sm text-charcoal-600 mt-1">{p.cta}</p>
          <p className="text-xs text-sage-700 mt-1">{p.hashtags}</p>
          {p.live_url && <a href={p.live_url} target="_blank" rel="noreferrer" className="text-xs text-terracotta underline" data-testid={`social-live-${p.id}`}>View live post</a>}
        </>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {p.status !== "published" && (
          <>
            <button data-testid={`social-genimage-${p.id}`} onClick={genImage} disabled={genning || genningVid}
              className="inline-flex items-center gap-1 rounded-full border border-terracotta/40 px-4 py-1.5 text-xs font-medium text-terracotta hover:bg-terracotta/5 disabled:opacity-60">
              <Sparkles size={13} className={genning ? "animate-pulse" : ""} /> {genning ? "Creating… (~1 min)" : p.image_path ? "Regenerate image" : "Generate image"}
            </button>
            <button data-testid={`social-genvideo-${p.id}`} onClick={genVideo} disabled={genning || genningVid}
              className="inline-flex items-center gap-1 rounded-full border border-charcoal-800/30 px-4 py-1.5 text-xs font-medium text-charcoal-800 hover:bg-charcoal-800/5 disabled:opacity-60">
              <Film size={13} className={genningVid ? "animate-pulse" : ""} /> {genningVid ? "Creating clip… (~2 min)" : p.media_type === "video" ? "Regenerate video" : "Generate video"}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" className="hidden"
              onChange={(e) => { uploadMedia(e.target.files?.[0]); e.target.value = ""; }} />
            <button data-testid={`social-upload-${p.id}`} onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-1 rounded-full border border-sand-300 px-4 py-1.5 text-xs font-medium text-charcoal-600 hover:border-terracotta hover:text-terracotta disabled:opacity-60">
              <Upload size={13} /> {uploading ? "Uploading…" : "Upload image / video"}
            </button>
          </>
        )}
        {editable && (
          <>
            <button onClick={save} className="rounded-full bg-charcoal-800 px-4 py-1.5 text-xs font-medium text-white hover:bg-charcoal-900">Save edits</button>
            <button data-testid={`social-approve-${p.id}`} onClick={() => setStatus("approved")} className="inline-flex items-center gap-1 rounded-full bg-sage px-4 py-1.5 text-xs font-medium text-white hover:bg-sage-hover"><Check size={13} /> Approve</button>
            <button data-testid={`social-reject-${p.id}`} onClick={() => setStatus("rejected")} className="inline-flex items-center gap-1 rounded-full border border-sand-300 px-4 py-1.5 text-xs text-charcoal-600 hover:bg-sand-100"><X size={13} /> Reject</button>
          </>
        )}
        {["approved", "scheduled"].includes(p.status) && (
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid={`social-publishnow-${p.id}`} onClick={publishNow} disabled={publishing}
              className="inline-flex items-center gap-1 rounded-full bg-terracotta px-4 py-1.5 text-xs font-medium text-white hover:bg-terracotta-hover disabled:opacity-60">
              <Send size={13} /> {publishing ? "Publishing…" : "Publish now"}
            </button>
            <button onClick={copyAll} className="inline-flex items-center gap-1 rounded-full border border-sand-300 px-4 py-1.5 text-xs text-charcoal-600 hover:bg-sand-100"><Copy size={13} /> Copy for posting</button>
            <input className={`${FIELD} w-64`} value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} placeholder="Live post URL after you publish" />
            <button data-testid={`social-published-${p.id}`} onClick={() => setStatus("published", { live_url: liveUrl })}
              className="inline-flex items-center gap-1 rounded-full bg-sage px-4 py-1.5 text-xs font-medium text-white hover:bg-sage-hover"><Send size={13} /> Mark published</button>
          </div>
        )}
        {p.status === "published" && <MetricsEditor post={p} reload={reload} />}
        <button onClick={remove} className="ml-auto p-1 text-charcoal-400 hover:text-red-600"><Trash2 size={14} /></button>
      </div>
    </Card>
  );
}

function MetricsEditor({ post, reload }) {
  const [open, setOpen] = useState(false);
  const [m, setM] = useState(post.metrics || {});
  const fields = ["impressions", "reach", "likes", "comments", "shares", "clicks"];
  const save = async () => {
    await api.put(`/admin/social/posts/${post.id}/metrics`, m);
    toast.success("Metrics saved");
    setOpen(false);
    reload();
  };
  if (!open) return <button onClick={() => setOpen(true)} className="rounded-full border border-sand-300 px-4 py-1.5 text-xs text-charcoal-600 hover:bg-sand-100">Enter metrics</button>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {fields.map((f) => (
        <input key={f} type="number" min={0} value={m[f] || ""} placeholder={f}
          onChange={(e) => setM({ ...m, [f]: Number(e.target.value) || 0 })}
          className="w-28 bg-white border border-sand-200 rounded-lg px-2 py-1.5 text-xs" />
      ))}
      <button onClick={save} className="rounded-full bg-sage px-3 py-1.5 text-xs text-white">Save</button>
    </div>
  );
}

function InsightsTab() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => { api.get("/admin/social/insights").then((r) => setData(r.data)); }, []);
  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data: res } = await api.post("/admin/social/sync-metrics");
      if (res.reason === "not_connected") toast.warning("Meta not connected yet — metrics will auto-sync every 6h once credentials are added.");
      else toast.success(`Synced metrics for ${res.synced} post(s).`);
      api.get("/admin/social/insights").then((r) => setData(r.data));
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };
  if (!data) return <p className="text-charcoal-500">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="social-insights">
      <div className="flex items-center justify-between">
        <p className="text-xs text-charcoal-500">Metrics auto-sync from connected platforms every 6 hours — or pull them on demand.</p>
        <button data-testid="social-sync-metrics" onClick={syncNow} disabled={syncing}
          className="rounded-full bg-charcoal-800 px-4 py-1.5 text-xs font-medium text-white hover:bg-charcoal-900 disabled:opacity-60">
          {syncing ? "Syncing…" : "Sync metrics now"}
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[["published_count", "Published posts", data.published_count],
          ["in_review", "Awaiting approval", data.by_status.in_review],
          ["approved", "Approved / scheduled", data.by_status.approved + data.by_status.scheduled],
          ["impressions", "Total impressions", data.metric_totals.impressions]].map(([k, label, v]) => (
          <Card key={k}><p className="font-serif text-3xl text-charcoal-800">{v}</p><p className="text-sm text-charcoal-500 mt-1">{label}</p></Card>
        ))}
      </div>
      <Card data-testid="social-insights-platforms">
        <h3 className="font-medium text-charcoal-800 mb-3">Performance by platform</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(data.by_platform || {}).map(([pl, count]) => (
            <div key={pl} className="rounded-xl border border-sand-200 px-4 py-3">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PLATFORM_STYLE[pl] || "bg-sand-200"}`}>{PLATFORM_LABELS[pl] || pl}</span>
              <p className="mt-2 text-sm text-charcoal-700">{count} post{count === 1 ? "" : "s"} in queue · {(data.published_by_platform || {})[pl] || 0} published</p>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-3">Engagement totals</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(data.metric_totals).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="capitalize text-charcoal-600">{k}</span><span className="text-charcoal-800">{v}</span></div>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-3">Content mix (pillars)</h3>
          <p className="text-xs text-charcoal-500 mb-3">Review your own content variety and representation balance over time.</p>
          <div className="space-y-2 text-sm">
            {Object.entries(data.by_pillar).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-charcoal-600">{k}</span><span className="text-charcoal-800">{v}</span></div>
            ))}
          </div>
        </Card>
      </div>
      {data.top_posts.length > 0 && (
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-3">Top posts (by impressions)</h3>
          <div className="space-y-2">
            {data.top_posts.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-charcoal-700 truncate">
                  <span className={`mr-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${PLATFORM_STYLE[p.platform] || "bg-sand-200"}`}>{PLATFORM_LABELS[p.platform] || p.platform}</span>
                  {p.caption}
                </span>
                <span className="shrink-0 text-xs text-charcoal-500">{p.metrics.impressions || 0} imp · {p.metrics.likes || 0} likes</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/social/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    setSaving(true);
    await api.put("/admin/social/settings", s);
    toast.success("Social settings saved");
    setSaving(false);
  };
  if (!s) return <p className="text-charcoal-500">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="social-settings">
      <Card>
        <div className="space-y-4">
          <label className="flex items-center gap-3 text-sm text-charcoal-700">
            <input type="checkbox" data-testid="social-auto" checked={!!s.auto_generate} onChange={(e) => setS({ ...s, auto_generate: e.target.checked })} />
            Auto-generate drafts daily (they always wait for your approval)
          </label>
          <label className="flex items-center gap-3 text-sm text-charcoal-700">
            <input type="checkbox" data-testid="social-video-music" checked={s.video_music !== false} onChange={(e) => setS({ ...s, video_music: e.target.checked })} />
            Add a soft background music bed to generated video clips
          </label>
          <label className="block text-sm text-charcoal-600">Posts per day
            <input type="number" min={1} max={6} className={`${FIELD} mt-1 w-24`} value={s.posts_per_day} onChange={(e) => setS({ ...s, posts_per_day: Math.max(1, Number(e.target.value) || 1) })} /></label>
          <label className="block text-sm text-charcoal-600">Posting times (comma separated, 24h)
            <input className={`${FIELD} mt-1`} value={(s.post_times || []).join(", ")} onChange={(e) => setS({ ...s, post_times: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></label>
          <label className="block text-sm text-charcoal-600">Content pillars / scenarios
            <input className={`${FIELD} mt-1`} value={(s.pillars || []).join(", ")} onChange={(e) => setS({ ...s, pillars: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></label>
          <label className="block text-sm text-charcoal-600">Reusable CTAs (one per line)
            <textarea rows={3} className={`${FIELD} mt-1`} value={(s.ctas || []).join("\n")} onChange={(e) => setS({ ...s, ctas: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} /></label>
          <label className="block text-sm text-charcoal-600">Brand voice & inclusivity guidance (used in every AI generation)
            <textarea rows={4} className={`${FIELD} mt-1`} value={s.brand_voice} onChange={(e) => setS({ ...s, brand_voice: e.target.value })} /></label>
        </div>
      </Card>
      <button data-testid="social-settings-save" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover disabled:opacity-60">
        <RefreshCw size={14} className={saving ? "animate-spin" : "hidden"} /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

function PlatformConnectionCard({ p, onSaved }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    const updates = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== "" || v === ""));
    const changed = Object.entries(values).filter(([, v]) => v.trim() !== "");
    if (!changed.length) { toast.warning("Paste at least one key first"); return; }
    setSaving(true);
    try {
      await api.put("/admin/social/credentials", Object.fromEntries(changed));
      toast.success(`${p.label} credentials saved securely`);
      setValues({});
      setTestResult(null);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
    return updates;
  };

  const clearKey = async (name) => {
    try {
      await api.put("/admin/social/credentials", { [name]: "" });
      toast.success(`${name} removed`);
      onSaved();
    } catch { toast.error("Could not remove key"); }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(`/admin/social/connections/${p.platform}/test`);
      setTestResult(r.data);
      if (r.data.ok) toast.success(`${p.label}: ${r.data.message}`);
      else toast.error(`${p.label}: ${r.data.message}`);
    } catch (e) { setTestResult({ ok: false, message: e?.response?.data?.detail || "Test failed" }); }
    setTesting(false);
  };

  return (
    <Card data-testid={`conn-card-${p.platform}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="font-medium text-charcoal-800 inline-flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${PLATFORM_STYLE[p.platform] || "bg-sand-200"}`}>{p.label}</span>
          {p.connected
            ? <span className="text-xs font-medium text-sage-700">✓ Connected</span>
            : <span className="text-xs font-medium text-clay-800">Not connected</span>}
        </h3>
        <button data-testid={`conn-test-${p.platform}`} onClick={test} disabled={testing || !p.connected}
          title={p.connected ? "Run a live API check" : "Add all keys first"}
          className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-4 py-1.5 text-xs font-medium text-charcoal-700 hover:bg-sand-100 disabled:opacity-40">
          <RefreshCw size={12} className={testing ? "animate-spin" : ""} /> {testing ? "Testing…" : "Test connection"}
        </button>
      </div>
      <p className="text-xs text-charcoal-500 mb-4">{p.help}</p>
      <div className="space-y-2.5">
        {p.keys.map((k) => (
          <div key={k.name}>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-charcoal-600">{k.name}
                {k.set && <span className="ml-2 font-normal text-sage-700">saved {k.masked && `(${k.masked})`}{k.source === "env" ? " · from env" : ""}</span>}
              </label>
              {k.set && k.source === "admin" && (
                <button data-testid={`conn-clear-${k.name}`} onClick={() => clearKey(k.name)} className="text-[11px] text-charcoal-400 hover:text-destructive">remove</button>
              )}
            </div>
            <input
              data-testid={`conn-input-${k.name}`}
              type="password"
              autoComplete="off"
              placeholder={k.set ? "•••••••• (paste to replace)" : "Paste value"}
              value={values[k.name] || ""}
              onChange={(e) => setValues({ ...values, [k.name]: e.target.value })}
              className={`${FIELD} mt-1`}
            />
          </div>
        ))}
      </div>
      {testResult && (
        <p data-testid={`conn-test-result-${p.platform}`} className={`mt-3 text-xs rounded-lg px-3 py-2 ${testResult.ok ? "bg-sage/10 text-sage-700" : "bg-red-50 text-red-700"}`}>
          {testResult.message}
        </p>
      )}
      <button data-testid={`conn-save-${p.platform}`} onClick={save} disabled={saving}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm font-medium text-white hover:bg-terracotta-hover disabled:opacity-60">
        {saving ? "Saving…" : "Save keys"}
      </button>
    </Card>
  );
}

function ConnectionsTab() {
  const [platforms, setPlatforms] = useState(null);

  const load = () => api.get("/admin/social/credentials").then((r) => setPlatforms(r.data)).catch(() => toast.error("Could not load credentials"));
  useEffect(() => { load(); }, []);

  if (!platforms) return <p className="text-sm text-charcoal-500">Loading…</p>;
  const connected = platforms.filter((p) => p.connected).length;

  return (
    <div data-testid="social-connections-tab">
      <div className="mb-5 rounded-2xl bg-sage-light/50 border border-sage/30 p-5">
        <p className="text-sm font-medium text-charcoal-800">{connected}/{platforms.length} platforms connected</p>
        <p className="mt-1 text-xs text-charcoal-600 max-w-2xl">
          Paste each platform's API keys below — they're encrypted before being stored and shown masked afterwards.
          Once a platform is connected, approving a post publishes it automatically. Use "Test connection" to verify keys against the live API without posting anything.
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        {platforms.map((p) => (
          <PlatformConnectionCard key={p.platform} p={p} onSaved={load} />
        ))}
      </div>
    </div>
  );
}
